/**
 * PerformanceHeatmap Component
 * Visual heatmap showing performance distribution across the organization
 */

import React from 'react';
import { clsx } from 'clsx';
import { Card } from '../../common';
import { Grid3X3 } from 'lucide-react';
import type { ScoreDistribution } from '../../../types';

export interface PerformanceHeatmapProps {
  distribution: ScoreDistribution;
  totalCount: number;
  title?: string;
  className?: string;
  isLoading?: boolean;
}

const CATEGORIES = [
  { key: 'excellent' as const, label: 'Excellent', range: '90-100', color: 'bg-green-500' },
  { key: 'good' as const, label: 'Good', range: '70-89', color: 'bg-blue-500' },
  { key: 'needsImprovement' as const, label: 'Needs Improvement', range: '50-69', color: 'bg-amber-500' },
  { key: 'poor' as const, label: 'Poor', range: '0-49', color: 'bg-red-500' },
];

export const PerformanceHeatmap: React.FC<PerformanceHeatmapProps> = ({
  distribution,
  totalCount,
  title = 'Performance Distribution',
  className,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-48 mb-6" />
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-stone-100 rounded" />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (totalCount === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-800 mb-4">{title}</h3>
        <div className="text-center py-8 text-stone-400">
          <Grid3X3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No performance data available</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <h3 className="text-lg font-semibold text-stone-800 mb-6">{title}</h3>

      <div className="grid grid-cols-2 gap-4">
        {CATEGORIES.map((category) => {
          const count = distribution[category.key];
          const percentage = totalCount > 0 ? (count / totalCount) * 100 : 0;

          return (
            <div
              key={category.key}
              className="relative overflow-hidden rounded-lg border border-stone-200 p-4"
            >
              {/* Background fill based on percentage */}
              <div
                className={clsx('absolute inset-0 opacity-10', category.color)}
                style={{ width: `${percentage}%` }}
              />

              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className={clsx('w-3 h-3 rounded-full', category.color)} />
                  <span className="text-sm font-medium text-stone-700">
                    {category.label}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-stone-800">
                    {count}
                  </span>
                  <span className="text-sm text-stone-500">
                    ({percentage.toFixed(1)}%)
                  </span>
                </div>
                <p className="text-xs text-stone-400 mt-1">
                  Score: {category.range}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bars visualization */}
      <div className="mt-6">
        <div className="flex rounded-full overflow-hidden h-4 bg-stone-100">
          {CATEGORIES.map((category) => {
            const count = distribution[category.key];
            const percentage = totalCount > 0 ? (count / totalCount) * 100 : 0;

            if (percentage === 0) return null;

            return (
              <div
                key={category.key}
                className={clsx(category.color, 'transition-all duration-500')}
                style={{ width: `${percentage}%` }}
                title={`${category.label}: ${count} (${percentage.toFixed(1)}%)`}
              />
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-xs text-stone-500">
          <span>Poor</span>
          <span>Excellent</span>
        </div>
      </div>

      {/* Total count */}
      <div className="mt-4 pt-4 border-t border-stone-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-stone-500">Total Employees</span>
          <span className="text-lg font-semibold text-stone-800">{totalCount}</span>
        </div>
      </div>
    </Card>
  );
};

export default PerformanceHeatmap;
